import { useCallback, useEffect, useRef, useState } from 'react';

import { ApiError } from '../../api/errors';
import { classifyApiError, type ApiProblemKind } from '../../api/problem';
import { EMPTY_CURSOR_STACK, pageNumber, popCursor, pushCursor, type CursorStack } from '../../components/cursor-stack';

/**
 * The state plumbing the four quality panels share — CLM-40.
 *
 * Wave 3's patterns, lifted verbatim rather than re-derived: the per-panel
 * `Panel<T>` union and `toProblem` (`NeedsAttention.tsx`), one `AbortController`
 * per effect keyed on the caller's dependencies plus a reload nonce
 * (`MatchReview.tsx`), and a cursor stack (`components/cursor-stack.ts`).
 *
 * Each panel owns its own state, so a 403 or a 501 on one leaves the other
 * three live — the reason there are four endpoints and not one (Q4). The
 * rendering half (`PanelProblem`, `Tile`) lives in `panel.tsx`, so this file
 * exports no component and fast refresh stays whole.
 */

export type Panel<TResponse> =
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly response: TResponse }
  | {
      readonly kind: 'problem';
      readonly problem: ApiProblemKind;
      readonly detail: string | null;
      readonly correlation_id: string | null;
    };

export function toProblem(error: unknown): Extract<Panel<never>, { kind: 'problem' }> {
  return {
    kind: 'problem',
    problem: classifyApiError(error),
    detail: error instanceof ApiError ? error.message : null,
    correlation_id: error instanceof ApiError ? error.correlation_id : null,
  };
}

/**
 * Run `load` once per change of `deps` (and on `reload()`), aborting the
 * previous call. The loader receives the signal it must pass to the wrapper.
 */
export function usePanel<TResponse>(
  load: (signal: AbortSignal) => Promise<TResponse>,
  deps: readonly unknown[],
): { readonly state: Panel<TResponse>; readonly reload: () => void } {
  const [state, setState] = useState<Panel<TResponse>>({ kind: 'loading' });
  const [nonce, setNonce] = useState(0);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;
    setState({ kind: 'loading' });

    void (async () => {
      try {
        const response = await load(controller.signal);
        if (controller.signal.aborted) return;
        setState({ kind: 'ready', response });
      } catch (error) {
        if (controller.signal.aborted) return;
        setState(toProblem(error));
      }
    })();

    return () => {
      controller.abort();
    };
    // `load` is a fresh closure every render; the caller names what it closes over.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  const reload = useCallback(() => {
    setNonce((n) => n + 1);
  }, []);

  return { state, reload };
}

/** A cursor stack with the three moves a pager needs. Filters reset it. */
export function usePaging(): {
  readonly cursor: string | undefined;
  readonly historyDepth: number;
  readonly page: number;
  readonly next: (nextCursor: string) => void;
  readonly previous: () => void;
  readonly reset: () => void;
} {
  const [stack, setStack] = useState<CursorStack>(EMPTY_CURSOR_STACK);
  const [cursor, setCursor] = useState<string | undefined>(undefined);

  const next = useCallback(
    (nextCursor: string) => {
      setStack((s) => pushCursor(s, nextCursor));
      setCursor(nextCursor);
    },
    [],
  );
  const previous = useCallback(() => {
    setStack((s) => {
      const popped = popCursor(s);
      setCursor(popped.cursor);
      return popped.stack;
    });
  }, []);
  const reset = useCallback(() => {
    setStack(EMPTY_CURSOR_STACK);
    setCursor(undefined);
  }, []);

  return { cursor, historyDepth: stack.cursors.length, page: pageNumber(stack), next, previous, reset };
}

/** The wave-3 copy for `forbidden` on the three governance panels (G19). */
export const FORBIDDEN_COPY =
  'Quality dashboards are for CEQ-Legal (legal_reviewer) and administrators (admin). Your role may read the ' +
  'contracts in your scope but not the search log, the judgement ledger or the outbound queue.';

